import React, { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { LayoutDashboard, MapPin, Grid3x3, Tags, Store, AlertTriangle, BookOpen, Mail, LogOut, Menu, X, DownloadCloud, Megaphone, Zap, Compass, BarChart3, Sparkles, Handshake, Bell, Crown, Shield, ImageOff, QrCode } from 'lucide-react';
import { adminApi, isAdminAuthed, adminLogout } from '../../api';

const NAV = [
  { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
  { to: '/admin/cities', label: 'Città', icon: MapPin },
  { to: '/admin/categories', label: 'Categorie', icon: Grid3x3 },
  { to: '/admin/subcategories', label: 'Sottocategorie', icon: Tags },
  { to: '/admin/activities', label: 'Attività', icon: Store },
  { to: '/admin/experiences', label: 'YUP Experiences', icon: Sparkles },
  { to: '/admin/banners', label: 'Banner Pubblicitari', icon: Megaphone },
  { to: '/admin/flash-offers', label: 'Offerte Flash', icon: Zap },
  { to: '/admin/itineraries', label: 'Itinerari', icon: Compass },
  { to: '/admin/partner-leads', label: 'Candidature Partner', icon: Handshake },
  { to: '/admin/sponsor-upgrades', label: 'Upgrade Sponsor', icon: Crown },
  { to: '/admin/moderation-logs', label: 'Moderazione', icon: Shield },
  { to: '/admin/broken-images', label: 'Foto rotte', icon: ImageOff },
  { to: '/admin/notifications', label: 'Notifiche', icon: Bell },
  { to: '/admin/analytics', label: 'Statistiche', icon: BarChart3 },
  { to: '/admin/stories', label: 'Storie', icon: BookOpen },
  { to: '/admin/emergency', label: 'Emergenze', icon: AlertTriangle },
  { to: '/admin/newsletter', label: 'Newsletter', icon: Mail },
  { to: '/admin/qr-pages', label: 'QR Freeversion', icon: QrCode },
  { to: '/admin/import', label: 'Import', icon: DownloadCloud }
];

export default function AdminLayout() {
  const nav = useNavigate();
  const loc = useLocation();
  const [mobileNav, setMobileNav] = useState(false);
  const [me, setMe] = useState(null);

  useEffect(() => {
    adminApi.me().then(setMe).catch(() => {});
  }, []);

  useEffect(() => {
    document.body.classList.add('admin-body');
    return () => document.body.classList.remove('admin-body');
  }, []);

  if (!isAdminAuthed()) return <Navigate to="/admin/login" replace />;

  const logout = () => { adminLogout(); nav('/admin/login'); };
  const isActive = (to, exact) => exact ? loc.pathname === to : loc.pathname.startsWith(to);

  return (
    <div className="admin-scope min-h-screen bg-slate-50 flex">
      {/* Mobile backdrop overlay */}
      {mobileNav && (
        <div
          className="fixed inset-0 bg-black/40 backdrop-blur-sm z-30 md:hidden"
          onClick={() => setMobileNav(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside className={`fixed md:static inset-y-0 left-0 w-72 sm:w-64 bg-white text-gray-700 border-r border-gray-200 z-40 transform transition-transform overflow-y-auto flex flex-col ${mobileNav ? 'translate-x-0 shadow-2xl' : '-translate-x-full md:translate-x-0'}`}>
        <div className="h-16 px-5 flex items-center justify-between border-b border-gray-200 flex-shrink-0">
          <Link to="/admin" className="flex items-center gap-2 font-bold text-lg text-[color:var(--yup-primary)]" onClick={() => setMobileNav(false)}><MapPin size={20} /> YUP CMS</Link>
          <button className="md:hidden p-2 hover:bg-gray-100 rounded text-gray-500 -mr-2" onClick={() => setMobileNav(false)} aria-label="Chiudi menu"><X size={22} /></button>
        </div>
        <nav className="p-3 space-y-0.5 flex-1">
          {NAV.map(n => {
            const active = isActive(n.to, n.exact);
            return (
              <Link key={n.to} to={n.to} onClick={() => setMobileNav(false)} className={`flex items-center gap-3 px-3 py-3 md:py-2.5 rounded-md text-sm font-medium transition-colors ${active ? 'bg-[color:var(--yup-primary)] text-white' : 'text-gray-600 hover:bg-gray-100 hover:text-[color:var(--yup-primary)]'}`}>
                <n.icon size={18} /> {n.label}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-gray-200 bg-white flex-shrink-0">
          <div className="text-xs text-gray-500 mb-2 truncate">{me?.email || 'admin'}</div>
          <button onClick={logout} className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-md bg-gray-100 hover:bg-gray-200 text-sm text-gray-700"><LogOut size={16} /> Logout</button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="h-16 bg-white border-b border-gray-200 sticky top-0 z-20 flex items-center justify-between px-3 md:px-8 gap-2">
          <button className="md:hidden p-2 hover:bg-gray-100 rounded flex-shrink-0" onClick={() => setMobileNav(true)} aria-label="Apri menu"><Menu size={22} /></button>
          <div className="font-semibold text-gray-800 text-sm md:text-base truncate">YUP CMS</div>
          <Link to="/" target="_blank" className="text-xs md:text-sm text-[color:var(--yup-primary)] hover:underline whitespace-nowrap">Vai al sito →</Link>
        </header>
        <main className="p-3 sm:p-4 md:p-8 max-w-full md:max-w-7xl overflow-x-hidden bg-slate-50 min-h-[calc(100vh-4rem)] text-gray-800"><Outlet /></main>
      </div>
    </div>
  );
}
